import { ServiceField, ServiceFieldType } from '@onlog-public/additional-services-types';
import optionsCacheCheckerAndLoader from '@pages/AdditionalServices/containers/services/optionsCacheCheckerAndLoader';
import optionsGenerator from '@pages/AdditionalServices/containers/services/optionsGenerator';
import { SelectOption } from '@pages/AdditionalServices/containers/services/optionsGenerator/types';
import { OptionsCache } from '@pages/AdditionalServices/containers/services/optionsLoader/types';
import { useEffect, useState } from 'react';
import { BehaviorSubject, Subject, bufferTime } from 'rxjs';
import { v4 } from 'uuid';

import { DefaultServiceOptionsState } from './DefaultServiceOptionsState';

/**
 * AdditionServicesOptionsState содержит данные стейта по селекторам полей
 * дополнительных услуг.
 */
export interface AdditionServicesOptionsState {
  /**
   * Поле содержит загруженные и подготовленные для отображения опции
   * селекторов для полей дополнительных услуг.
   * Поле формируется автоматически, исходя из переданных в шину полей
   * для загрузки данных.
   */
  FieldOptions: { [T in string]: SelectOption[] };

  /**
   * FieldsToCalculateState содержит данные полей, для которых необходимо
   * загрузить сущности опций и сформировать их.
   */
  FieldsToCalculateState: ServiceField[];

  /**
   * Язык, для которого необходимо выполнять формирование опций.
   */
  LangID: string;

  /**
   * Кэш сущностей различного типа для использования в формировании опций
   * для полей дополнительных услуг.
   */
  Cache: { [T in ServiceFieldType]: OptionsCache<any> };
  Loadings: string[]; // Идентификаторы загрузок сущностей.
}

const recalculateFieldOptions$ = new Subject<boolean>();
const loadingForType$ = new Subject<{ type: ServiceFieldType; id?: string[] }>();
export const optionsState$ = new BehaviorSubject<AdditionServicesOptionsState>({
  ...new DefaultServiceOptionsState(),
});

/**
 * exportServiceFieldOptions выполняет экспорт опций полей для переданного
 * списка. Используется при формировании данных для добавления в коризу.
 *
 * @param fields
 * @param values
 */
export const exportServiceFieldOptions = (
  fields: ServiceField[],
  values: { [T in string]: number }
): { [T in string]: SelectOption[] } => {
  const result: { [T in string]: SelectOption[] } = {};

  const state = optionsState$.getValue();
  fields.map((f) => {
    result[f.code] = state.FieldOptions[f.code]?.filter((o) => values[f.code] === o.value) ?? [];
    if (
      result[f.code].length === 0 &&
      !!values[f.code] &&
      !['switch', 'number', 'hidden'].includes(f.type)
    ) {
      throw new Error(`failed to get cache data for field: ${f.code} :: ${f.type}`);
    }
  });

  return result;
};

/**
 * makeOptionsForFields реализует функционал загрузки опций для переданного
 * списка полей дополнительных услуг. Метод лишь инициирует загрузку.
 * Сама загрузка выполняется по шине loadingForType$
 *
 * @param fields
 */
export const makeOptionsForFields = (fields: ServiceField[]) => {
  fields.map((field) => {
    if (['switch', 'hidden', 'number'].includes(field.type)) {
      return;
    }

    let loadingID = field.handbook_limitations.length > 0 ? field.handbook_limitations : undefined;

    if (field.type === 'handbook') {
      loadingID = [String(field.handbook_id)];
    }

    if (field.type === 'location' && !loadingID) {
      loadingID = [String(field.value)];
    }

    if (['package', 'product'].includes(field.type) && !loadingID) {
      loadingID = [String(field.value)];
    }

    loadingForType$.next({
      type: field.type,
      id: loadingID,
    });
  });

  optionsState$.next({
    ...optionsState$.getValue(),
    FieldsToCalculateState: [...fields],
  });
  recalculateFieldOptions$.next(true);
};

/**
 * makeOptionsForEcoFeeChanges метод позволяет асинхронно подгружать
 * изменения по полям товаров и упаковок. В данных полях используется поиск с
 * подсказкой и изначально кэш по ним не собирается. Данный метод
 * позволяет подгружать в кэш данные по выбранным в таких полях товарам и локациях.
 * @param field
 * @param value
 */
export const makeOptionsForEcoFeeChanges = (field: ServiceField, value: number) => {
  if (!['package', 'product'].includes(field.type) || value === 0) {
    return;
  }

  const state = optionsState$.getValue();
  const fieldsToCalculateState = [...state.FieldsToCalculateState, { ...field, value: value }];

  optionsState$.next({
    ...state,
    FieldsToCalculateState: fieldsToCalculateState,
  });
  loadingForType$.next({
    type: field.type,
    id: [String(value)],
  });
};

/**
 * makeOptionsForLocationChanges метод позволяет асинхронно подгружать
 * изменения по полям локаций. В данных полях используется поиск с
 * подсказкой и изначально кэш по ним не собирается. Данный метод
 * позволяет подгружать в кэш данные по выбранным в таких полях локациям.
 * @param field
 * @param value
 */
export const makeOptionsForLocationChanges = (field: ServiceField, value: number) => {
  if (field.type !== 'location' || value === 0) {
    return;
  }

  const state = optionsState$.getValue();
  const fieldsToCalculateState = [...state.FieldsToCalculateState, { ...field, value: value }];

  optionsState$.next({
    ...state,
    FieldsToCalculateState: fieldsToCalculateState,
  });
  loadingForType$.next({
    type: field.type,
    id: [String(value)],
  });
};

/**
 * loadCacheForType выполняет загрузку данных сущностей в кеш по переданному типу поля
 * и идентификаторам. Метод не загружает данные, а лишь инициирует загрузку.
 * Сама загрузка выполняется по шине loadingForType$
 *
 * @param type
 * @param id
 */
export const loadCacheForType = (type: ServiceFieldType, id: string[]) => {
  loadingForType$.next({
    type: type,
    id: id,
  });
};

export const initOptions = (langID: string) => {
  // Получаем все параметры и загружаем стейт по каждому.
  loadingForType$.pipe(bufferTime(50)).subscribe({
    next: (batch) => {
      if (batch.length == 0) {
        return;
      }

      const existsCache = optionsState$.getValue();
      const id = v4();
      optionsState$.next({
        ...existsCache,
        Loadings: [...existsCache.Loadings, id],
      });

      getNewStateWithLoadedCache(batch, id).then(({ isNeedSave, newState }) => {
        const state = optionsState$.getValue();

        if (!isNeedSave) {
          return optionsState$.next({
            ...state,
            Loadings: state.Loadings.filter((l) => l !== id),
          });
        }

        optionsState$.next(newState);
        recalculateFieldOptions$.next(true);
      });
    },
  });

  // Подписка на пересчет опций для полей после загрузки данных сущностей.
  recalculateFieldOptions$.pipe(bufferTime(50)).subscribe({
    next: (buffer) => {
      if (0 == buffer.length) {
        return;
      }

      const state = optionsState$.getValue();
      if (state.FieldsToCalculateState.length === 0) {
        return;
      }

      const options = getOptions(state);

      setOptions(state, options);
    },
  });

  optionsState$.next({
    ...optionsState$.getValue(),
    LangID: langID,
  });
};

/**
 * getNewStateWithLoadedCache выполняет загрузку опций и формирует
 * новый стейт optionsState$ с кэшом
 *
 * @param batch
 * @param id
 * @param fields
 */
export const getNewStateWithLoadedCache = async (
  batch: { type: ServiceFieldType; id?: string[] }[],
  id: string,
  fields?: ServiceField[]
) => {
  const existsCache = optionsState$.getValue();

  const values = await Promise.all(
    batch.map(async (conf) => {
      return {
        type: conf.type,
        cache: await optionsCacheCheckerAndLoader().LoadCacheForNewData(
          conf.type,
          !existsCache.Cache[conf.type].isPartialLoading,
          Object.keys(existsCache.Cache[conf.type]),
          conf.id
        ),
      };
    })
  );

  const state = optionsState$.getValue();
  const newState = {
    ...state,
    Loadings: state.Loadings.filter((l) => l !== id),
  };

  if (fields && fields.length > 0) {
    newState.FieldsToCalculateState = [...fields];
  }

  let isNeedSave = false;
  values.map((val) => {
    if (!val.cache.isPartialLoading) {
      isNeedSave = true;
      newState.Cache[val.type] = val.cache;
      return;
    }

    if (Object.keys(val.cache.cache).length === 0) {
      return;
    }

    if (!state.Cache[val.type].isPartialLoading) {
      return;
    }

    isNeedSave = true;
    newState.Cache[val.type] = {
      isPartialLoading: true,
      cache: {
        ...newState.Cache[val.type].cache,
        ...val.cache.cache,
      },
    };
  });

  return { isNeedSave, newState };
};

/**
 * getOptions генерирует опции для полей селекторов
 *
 * @param state
 */
export const getOptions = (state: AdditionServicesOptionsState) => {
  const options: { [T in string]: SelectOption[] } = { ...state.FieldOptions };
  state.FieldsToCalculateState.map((field) => {
    options[field.code] = optionsGenerator().getOptions(
      field,
      state.LangID,
      state.Cache[field.type]
    );
  });

  return options;
};

/**
 * setOptions устанавливает сгенерированные опции в
 * стейт optionsState$
 *
 * @param state
 * @param options
 */
export const setOptions = (
  state: AdditionServicesOptionsState,
  options: { [T in string]: SelectOption[] }
) => {
  optionsState$.next({
    ...state,
    FieldOptions: options,
  });
};

const useAServiceOptions = () => {
  const [options, setState] = useState<AdditionServicesOptionsState>(optionsState$.getValue());
  useEffect(() => {
    const subscription = optionsState$.pipe().subscribe({
      next: (nState) => setState(nState),
    });

    return () => {
      try {
        subscription?.unsubscribe();
      } catch (e) {}
    };
  }, []);

  return {
    options,
    loadCacheForType,
  };
};

export default useAServiceOptions;
